import { useEffect, useMemo, useState } from "react";
import {
  applyCustomization, buildChartData, detectInsights, suggestChartSpecs,
  type ChartCustomization, type ChartInsight, type ChartKind, type ChartSpec,
} from "@brasilnovo/shared";
import { ChartRenderer } from "./ChartRenderer.js";
import { useChartPrefs } from "../lib/chartPrefs.js";

const KIND_LABEL: Record<ChartKind, string> = {
  line: "Linha", area: "Área", bar: "Barras", stackedBar: "Empilhado",
  groupedBar: "Agrupado", horizontalBar: "Ranking", pie: "Pizza",
  scatter: "Dispersão", kpi: "Indicador", table: "Tabela",
};
const KIND_ICON: Record<ChartKind, string> = {
  line: "📈", area: "🏔", bar: "📊", stackedBar: "🧱", groupedBar: "📊",
  horizontalBar: "📶", pie: "🥧", scatter: "✴", kpi: "🔢", table: "▦",
};
const BAR_FAMILY = new Set<ChartKind>(["bar", "stackedBar", "groupedBar", "horizontalBar"]);
const SEV_STYLE: Record<ChartInsight["severity"], string> = {
  critical: "bg-red-50 text-red-700 ring-red-200",
  warning: "bg-amber-50 text-amber-800 ring-amber-200",
  info: "bg-blue-50 text-blue-700 ring-blue-200",
};
const SEV_ICON: Record<ChartInsight["severity"], string> = { critical: "⛔", warning: "⚠", info: "💡" };

export function ChartCard({
  chartKey, rows, columns, title, specs, height = 320,
}: {
  chartKey: string;
  rows: Record<string, unknown>[];
  columns: string[];
  title?: string;
  /** specs sugeridos; se omitido, inferimos pela forma dos dados. */
  specs?: ChartSpec[];
  height?: number;
}) {
  const { custom, save, reset } = useChartPrefs(chartKey);
  const [local, setLocal] = useState<ChartCustomization>(custom);

  // Sincroniza o estado local quando as prefs chegam do servidor.
  useEffect(() => { setLocal(custom); }, [JSON.stringify(custom)]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSpecs = useMemo(
    () => (specs?.length ? specs : suggestChartSpecs(columns, rows)),
    [specs, columns, rows],
  );
  const kinds = useMemo(() => {
    const seen = new Set<ChartKind>();
    return allSpecs.filter((s) => (seen.has(s.kind) ? false : (seen.add(s.kind), true))).map((s) => s.kind);
  }, [allSpecs]);

  const activeKind = local.kind ?? allSpecs[0]?.kind ?? "table";
  const baseSpec = allSpecs.find((s) => s.kind === activeKind) ?? allSpecs[0] ?? { kind: "table" as ChartKind, series: [] };
  const effectiveSpec = applyCustomization(baseSpec, local);

  const insights = useMemo(() => detectInsights(effectiveSpec, rows, columns), [effectiveSpec, rows, columns]);
  // Séries disponíveis (para toggles), a partir dos dados preparados sem ocultar.
  const seriesKeys = useMemo(() => buildChartData(effectiveSpec, rows).seriesKeys, [effectiveSpec, rows]);

  const update = (patch: Partial<ChartCustomization>) => {
    const next: ChartCustomization = { ...local, ...patch };
    setLocal(next);
    save(next);
  };
  const doReset = () => { setLocal({}); reset(); };

  const showSort = BAR_FAMILY.has(activeKind);
  const showTopN = ["horizontalBar", "pie", "groupedBar", "stackedBar"].includes(activeKind);
  const showStack = activeKind === "stackedBar" || activeKind === "groupedBar";
  const showAxis = ["bar", "stackedBar", "groupedBar", "horizontalBar", "line", "area"].includes(activeKind);
  const showSeriesToggle = seriesKeys.length > 1 && seriesKeys[0] !== "__count__";

  return (
    <div className="card p-5" data-testid="chart-card" data-chart-key={chartKey}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          {title && <h3 className="font-semibold text-slate-800">{title}</h3>}
          <div className="text-xs text-slate-400" data-testid="chart-active-kind">{KIND_LABEL[activeKind]}</div>
        </div>
        <button className="text-xs text-slate-400 hover:text-slate-700" data-testid="chart-reset" onClick={doReset} title="Restaurar padrão">
          ↺ padrão
        </button>
      </div>

      {/* Selos de singularidade (insights apontando o que destoa). */}
      {insights.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="chart-insights">
          {insights.map((ins, i) => (
            <span key={i} data-testid="chart-insight" data-severity={ins.severity}
              className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ring-1 ${SEV_STYLE[ins.severity]}`}>
              <span aria-hidden>{SEV_ICON[ins.severity]}</span> {ins.text}
            </span>
          ))}
        </div>
      )}

      {/* Barra de controles (customização em tempo real). */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2 text-xs" data-testid="chart-controls">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Tipo de gráfico">
          {kinds.map((k) => (
            <button key={k} data-testid={`chart-kind-${k}`}
              className={`rounded-md px-2 py-1 font-medium transition ${k === activeKind ? "bg-brand-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"}`}
              onClick={() => update({ kind: k })} title={KIND_LABEL[k]}>
              {KIND_ICON[k]} {KIND_LABEL[k]}
            </button>
          ))}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {showSort && (
            <label className="flex items-center gap-1 text-slate-500">
              ordenar
              <select className="rounded border border-slate-300 px-1.5 py-1" data-testid="chart-sort"
                value={local.sort ?? "none"} onChange={(e) => update({ sort: e.target.value as ChartCustomization["sort"] })}>
                <option value="none">original</option>
                <option value="desc">maior→menor</option>
                <option value="asc">menor→maior</option>
              </select>
            </label>
          )}
          {showTopN && (
            <label className="flex items-center gap-1 text-slate-500">
              top-N
              <input type="number" min={1} max={50} className="w-16 rounded border border-slate-300 px-1.5 py-1" data-testid="chart-topn"
                value={local.topN ?? ""} placeholder="auto"
                onChange={(e) => update({ topN: e.target.value ? Number(e.target.value) : undefined })} />
            </label>
          )}
          {showStack && (
            <label className="flex items-center gap-1 text-slate-500" data-testid="chart-stack-label">
              <input type="checkbox" data-testid="chart-stack" checked={activeKind === "stackedBar"}
                onChange={(e) => update({ kind: e.target.checked ? "stackedBar" : "groupedBar" })} />
              empilhar
            </label>
          )}
          {showAxis && (
            <span className="flex items-center gap-1 text-slate-500">
              eixo
              <input type="number" className="w-20 rounded border border-slate-300 px-1.5 py-1" placeholder="mín" data-testid="chart-ymin"
                value={local.yMin ?? ""} onChange={(e) => update({ yMin: e.target.value === "" ? null : Number(e.target.value) })} />
              <input type="number" className="w-20 rounded border border-slate-300 px-1.5 py-1" placeholder="máx" data-testid="chart-ymax"
                value={local.yMax ?? ""} onChange={(e) => update({ yMax: e.target.value === "" ? null : Number(e.target.value) })} />
            </span>
          )}
        </div>
      </div>

      {/* Toggles de série (ocultar/mostrar séries empilhadas/agrupadas). */}
      {showSeriesToggle && (
        <div className="mb-2 flex flex-wrap gap-2 text-xs" data-testid="chart-series-toggles">
          {seriesKeys.map((k) => {
            const hidden = (local.hiddenSeries ?? []).includes(k);
            return (
              <button key={k} data-testid={`chart-series-${k}`}
                className={`rounded-full px-2 py-0.5 ring-1 ${hidden ? "bg-white text-slate-300 ring-slate-200 line-through" : "bg-slate-100 text-slate-600 ring-slate-200"}`}
                onClick={() => {
                  const set = new Set(local.hiddenSeries ?? []);
                  hidden ? set.delete(k) : set.add(k);
                  update({ hiddenSeries: [...set] });
                }}>
                {k}
              </button>
            );
          })}
        </div>
      )}

      <ChartRenderer spec={effectiveSpec} rows={rows} columns={columns} custom={local} height={height} />

      {effectiveSpec.reason && effectiveSpec.kind !== "table" && (
        <div className="mt-1.5 text-xs text-slate-400">Por que este gráfico: {effectiveSpec.reason}.</div>
      )}
    </div>
  );
}
